import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import 'styles/common.css';
import './Contact.css';

const Contact = () => {
  const GOOGLE_APPS_SCRIPT_URL = `https://script.google.com/macros/s/AKfycbydcQgoCaUeiQ0SHtLn_La-_IYG6D7vT4U2abVJG9GQBCYNeZy0Wd56umnMXtBmBdf36g/exec`;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    company: '',
    country: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Please complete this required field.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);

      // Using URLSearchParams for better compatibility with Google Apps Script e.parameter
      const formDetails = new URLSearchParams();
      Object.keys(formData).forEach(key => {
        formDetails.append(key, formData[key]);
      });

      try {
        // Using mode: 'no-cors' with URLSearchParams is the most robust way to submit
        await fetch(GOOGLE_APPS_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          body: formDetails,
        });

        // Since we are using 'no-cors', we cannot read the response or check response.ok
        // We assume success if the fetch promise resolves without throwing
        setSubmitSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          company: '',
          country: '',
          message: ''
        });
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-header">
          <Link to="/" className="back-button">← Back to Home</Link>
          <h1 className="contact-title">Contact Us</h1>
          <p className="contact-subtitle">Get in touch with our team to learn more about our services</p>
        </div>

        <div className="contact-content">
          <div className="contact-layout">
            <div className="contact-info-section">
              <div className="contact-info-card">
                <h2>Get In Touch</h2>
                <p className="contact-info-description">
                  Ready to revolutionize your research with AI and quantum computing?
                  Our team is here to answer your questions and discuss how we can help
                  accelerate your projects.
                </p>

                <div className="contact-details">
                  <div className="contact-detail-card">
                    <h3>📍 Location</h3>
                    <p>Maryland, United States</p>
                  </div>
                  <div className="contact-detail-card">
                    <h3>📞 Phone</h3>
                    <p>240-686-5707</p>
                  </div>
                  <div className="contact-detail-card">
                    <h3>📧 Email</h3>
                    <p><a href="mailto:genionquantumtech@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>genionquantumtech@gmail.com</a></p>
                  </div>
                  <div className="contact-detail-card">
                    <h3>⏰ Business Hours</h3>
                    <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                  </div>
                  <div className="contact-detail-card">
                    <h3>💼 Services</h3>
                    <p>AI-Driven Drug Design</p>
                    <p>Quantum Computing Solutions</p>
                    <p>Environmental Monitoring</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <div className="contact-form-card">
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2>Send us a Message</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor='firstName' className="form-label" required>
                        First Name
                      </label>
                      <input
                        type='text'
                        id='firstName'
                        name='firstName'
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`form-input ${errors.firstName ? 'error' : ''}`}
                        placeholder='Enter your first name'
                      />
                      {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor='lastName' className="form-label" required>
                        Last Name
                      </label>
                      <input
                        type='text'
                        id='lastName'
                        name='lastName'
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`form-input ${errors.lastName ? 'error' : ''}`}
                        placeholder='Enter your last name'
                      />
                      {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor='email' className="form-label" required>
                      Email
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder='Enter your email'
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor='phone' className="form-label">
                        Phone
                      </label>
                      <input
                        type='tel'
                        id='phone'
                        name='phone'
                        value={formData.phone}
                        onChange={handleChange}
                        className={`form-input ${errors.phone ? 'error' : ''}`}
                        placeholder='Enter your phone number'
                      />
                      {errors.phone && <span className="error-text">{errors.phone}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor='position' className="form-label">
                        Position
                      </label>
                      <input
                        type='text'
                        id='position'
                        name='position'
                        value={formData.position}
                        onChange={handleChange}
                        className={`form-input ${errors.position ? 'error' : ''}`}
                        placeholder='Enter your position'
                      />
                      {errors.position && <span className="error-text">{errors.position}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor='company' className="form-label">
                        Company
                      </label>
                      <input
                        type='text'
                        id='company'
                        name='company'
                        value={formData.company}
                        onChange={handleChange}
                        className={`form-input ${errors.company ? 'error' : ''}`}
                        placeholder='Enter your company'
                      />
                      {errors.company && <span className="error-text">{errors.company}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor='country' className="form-label">
                        Country
                      </label>
                      <input
                        type='text'
                        id='country'
                        name='country'
                        value={formData.country}
                        onChange={handleChange}
                        className={`form-input ${errors.country ? 'error' : ''}`}
                        placeholder='Enter your country'
                      />
                      {errors.country && <span className="error-text">{errors.country}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor='message' className="form-label" required>
                      Message
                    </label>
                    <textarea
                      id='message'
                      name='message'
                      value={formData.message}
                      onChange={handleChange}
                      className={`form-textarea ${errors.message ? 'error' : ''}`}
                      placeholder='Tell us about your project or inquiry...'
                      rows="5"
                    />
                    {errors.message && <span className="error-text">{errors.message}</span>}
                  </div>

                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className={`btn btn-primary submit-button ${isSubmitting ? 'submitting' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Submitting...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>

                  {submitSuccess && (
                    <div className="success-message">
                      <div className="success-icon">✓</div>
                      <div>
                        <h3>Thank You!</h3>
                        <p>Your message has been sent successfully. Our team will get back to you soon.</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;